import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { z } from "zod";
import { PlannerErrorPage } from "@/components/planner-error-page";
import { TakeoffToolLayout } from "@/components/takeoff-app-sidebar";
import { TakeoffTrashList } from "@/components/takeoff-trash-list";
import { orpc } from "@/utils/orpc";

const projectIdSchema = z.uuid();

export const Route = createFileRoute("/project/$projectId/trash")({
	component: ProjectTrashRoute,
});

function ProjectTrashRoute(): ReactElement {
	const { projectId } = Route.useParams();
	const parsed = projectIdSchema.safeParse(projectId);
	if (!parsed.success) {
		return (
			<PlannerErrorPage
				description="The planner link is missing a valid project id."
				title="Invalid project link"
			/>
		);
	}
	return (
		<TakeoffToolLayout projectId={parsed.data}>
			<ProjectTrashList projectId={parsed.data} />
		</TakeoffToolLayout>
	);
}

const ProjectTrashList = ({
	projectId,
}: {
	projectId: string;
}): ReactElement => {
	const queryClient = useQueryClient();
	const trashQuery = useQuery(
		orpc.takeoffs.list.queryOptions({
			input: { deleted: true, projectId },
		}),
	);
	// Restore moves rows back to the live list — invalidate both keys.
	const invalidateLists = async (): Promise<void> => {
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: orpc.takeoffs.list.queryOptions({ input: { projectId } })
					.queryKey,
			}),
			queryClient.invalidateQueries({
				queryKey: orpc.takeoffs.list.queryOptions({
					input: { deleted: true, projectId },
				}).queryKey,
			}),
		]);
	};
	const restoreTakeoff = useMutation(
		orpc.takeoffs.restore.mutationOptions({ onSuccess: invalidateLists }),
	);
	const permanentDeleteTakeoff = useMutation(
		orpc.takeoffs.permanentDelete.mutationOptions({
			onSuccess: invalidateLists,
		}),
	);

	if (trashQuery.isLoading) {
		return <p className="p-6">Loading…</p>;
	}
	if (trashQuery.isError) {
		return (
			<PlannerErrorPage
				description="Trashed Takeoffs could not be loaded for this project."
				title="Trash unavailable"
			/>
		);
	}

	return (
		<TakeoffTrashList
			onPermanentDelete={(takeoffId) =>
				permanentDeleteTakeoff.mutate({ takeoffId })
			}
			onRestore={(takeoffId) => restoreTakeoff.mutate({ takeoffId })}
			trashedTakeoffs={(trashQuery.data ?? []).flatMap((row) =>
				// trash query returns the metadata-only projection; skip any
				// full-shape rows (cache overlap) defensively.
				"deletedAt" in row && row.deletedAt
					? [
							{
								deletedAt: String(row.deletedAt),
								discipline: row.discipline,
								id: row.id,
								name: row.name,
							},
						]
					: [],
			)}
		/>
	);
};
